import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Table, Tag, Typography, EmptyState, Switch, Space, Tooltip } from '../../ui';
import type { TagTone } from '../../ui';
import { Link } from 'react-router-dom';
import { request } from '../../api/client';
import { formatIst } from '../../lib/time';
import { fmtQty } from '../../lib/format';
import { useAuthStore } from '../../store/auth';

interface ShortLine { productId: string; productName: string | null; shortQty: number }
interface ShortfallRow {
  id: string;
  orderId: string;
  orderNo: string;
  warehouseId: string;
  warehouseName: string | null;
  warehouseCode: string | null;
  status: 'OPEN' | 'RESOLVED';
  outcome: string | null;
  reportedVia: string;
  shortSnapshot: ShortLine[] | string | null;
  pendingTotal: string;
  reportedById: string | null;
  reportedByName: string | null;
  reportedAt: string | null;
  remarks: string | null;
  resolution: string | null;
  resolvedAt: string | null;
  orderStatus: string;
  retailerName: string | null;
}

const OUTCOME_LABEL: Record<string, { text: string; color: string }> = {
  NEEDS_TRANSFER: { text: 'Needs transfer', color: 'gold' },
  INSUFFICIENT_TOTAL: { text: 'No stock anywhere', color: 'red' },
  FULLY_COVERED_SINGLE: { text: 'Reassign warehouse', color: 'blue' },
};
const VIA_LABEL: Record<string, string> = {
  MANUAL: 'Reported', AUTO_PACK: 'Partial pack', AUTO_DISPATCH: 'Partial dispatch', AUTO: 'Detected',
};

function parseSnapshot(s: ShortfallRow['shortSnapshot']): ShortLine[] {
  if (!s) return [];
  if (typeof s === 'string') { try { return JSON.parse(s) as ShortLine[]; } catch { return []; } }
  return Array.isArray(s) ? s : [];
}

/**
 * Fulfillment V2 · Phase 2.5 — the persisted shortfall QUEUE. Planners/admins see every open shortfall
 * (their worklist); a Warehouse Manager sees only shortfalls at their assigned warehouse(s), read-only.
 * There is no "resolve" action — a shortfall clears automatically the moment the order is no longer short
 * (consolidated / restocked / reassigned / fulfilled) or the order is cancelled. Planners act on the ORDER.
 */
export function ShortfallsQueuePage() {
  const [showResolved, setShowResolved] = useState(false);
  const role = useAuthStore((s) => s.user?.role);
  const isWhm = role === 'WAREHOUSE_MANAGER';
  const { data, isLoading } = useQuery({
    queryKey: ['shortfalls', showResolved ? 'all' : 'open'],
    queryFn: () => request<ShortfallRow[]>({ url: `/shortfalls${showResolved ? '?status=all' : ''}` }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const rows = data ?? [];

  return (
    <div>
      <Typography.Title level={4} style={{ marginBottom: 2 }}>Shortfalls</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        {isWhm
          ? 'Shortfalls at your warehouse(s). These are resolved by a planner — they clear automatically once the order is no longer short.'
          : 'Orders that can’t be fully packed at their warehouse. Open the order to consolidate stock, add stock, or reassign the warehouse — each entry clears automatically once the order is no longer short.'}
      </Typography.Paragraph>
      <Card>
        <Space style={{ marginBottom: 12 }}>
          <Switch checked={showResolved} onCheckedChange={setShowResolved} />
          <span>Show resolved</span>
        </Space>
        <Table<ShortfallRow>
          rowKey="id"
          size="small"
          loading={isLoading}
          dataSource={rows}
          pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} ${showResolved ? 'total' : 'open'}` }}
          locale={{ emptyText: <EmptyState title="No open shortfalls — all orders coverable." /> }}
          columns={[
            {
              title: 'Order', dataIndex: 'orderNo', width: 150,
              render: (v: string, r) => (
                <div>
                  <Link to={`/orders/${r.orderId}`} style={{ fontWeight: 600 }}>{v}</Link>
                  <div style={{ fontSize: 12, color: '#8c8c8c' }}>{r.retailerName ?? '—'}</div>
                </div>
              ),
            },
            {
              title: 'Warehouse', dataIndex: 'warehouseName', width: 150,
              render: (v: string | null, r) => <Tag tone="blue">{v ?? r.warehouseCode ?? r.warehouseId}</Tag>,
            },
            {
              title: 'Short', dataIndex: 'shortSnapshot', width: 200,
              render: (_: any, r) => {
                const lines = parseSnapshot(r.shortSnapshot);
                if (!lines.length) return <Tag tone="red">short {fmtQty(Number(r.pendingTotal))}</Tag>;
                return (
                  <Space size={[4, 4]} wrap>
                    {lines.map((l, i) => (
                      <Tag tone="red" key={i} style={{ margin: 0 }}>{fmtQty(Number(l.shortQty))} × {l.productName ?? l.productId}</Tag>
                    ))}
                  </Space>
                );
              },
            },
            {
              title: 'Why', dataIndex: 'outcome', width: 160,
              render: (v: string | null) => {
                const o = v ? OUTCOME_LABEL[v] : null;
                return o ? <Tag tone={o.color as TagTone}>{o.text}</Tag> : (v ? <Tag>{v}</Tag> : '—');
              },
            },
            {
              // No fixed width — this column absorbs the table's spare horizontal room so the via + who + when
              // sit on ONE line and the remark gets a wide single line (truncated, full text on hover) instead
              // of wrapping to 3–4 lines. That's what was making the rows tall.
              title: 'Reported',
              render: (_: any, r) => (
                <div>
                  <div style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <Tag style={{ margin: 0 }}>{VIA_LABEL[r.reportedVia] ?? r.reportedVia}</Tag>
                    <span>{r.reportedByName ?? ''}</span>
                    {r.reportedAt && <span style={{ color: '#8c8c8c', fontSize: 12 }}>· {formatIst(r.reportedAt)}</span>}
                  </div>
                  {r.remarks ? (
                    <Tooltip label={r.remarks}>
                      <div style={{ fontSize: 12, color: '#8c8c8c', fontStyle: 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 460 }}>“{r.remarks}”</div>
                    </Tooltip>
                  ) : null}
                </div>
              ),
            },
            {
              title: 'Status', dataIndex: 'status', width: 130,
              render: (v: string, r) =>
                v === 'OPEN'
                  ? <Tag tone="red">Open</Tag>
                  : (
                    <Tooltip label={r.resolution === 'AUTO_CANCELLED' ? 'Order cancelled/closed' : r.resolution === 'AUTO' ? 'Order no longer short' : r.resolution ?? ''}>
                      <Tag tone="green">Resolved</Tag>
                    </Tooltip>
                  ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
